import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';

import { Badge, Button, Card, CardBody, CardHeader, CardText, Container } from 'reactstrap';

// Font Awesome icons.
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import RegistrationContent from '../components/RoleRegistration/RegistrationContent';
import CongratulationsContent from '../components/RoleRegistration/CongratulationsContent';
import RoleDescriptionPanel from '../components/RoleDescriptionPanel';

import { DEFAULT_ROLE_WIZARD_DATA, BETA_ROLES } from '../utils/constants/betaRoles';
import wizardContentData from '../components/RoleRegistration';
import auth, { getUserEmail, getUserId, hasDisplayName, isEmailVerified, isLoggedIn } from '../utils/firebase/auth';
import { getRoleUsersCount, getUserBetaRoleId, getWaitListCount, isBetaUser, storeBetaUser } from '../utils/firebase/firestore';
import ExistingRoleContent from '../components/RoleRegistration/ExistingRoleContent';
import SignupCapContent from '../components/RoleRegistration/SignupCapContent';
import ShareButton from '../components/ShareButton';
import { founding50RoleDescription } from '../utils/constants/founding50RoleDescription';
import { dayOnesRoleDescription } from '../utils/constants/dayOnesRoleDescription';
import { influencersRoleDescription } from  '../utils/constants/influencersRoleDescription';
import { leagueHostsRoleDescription } from '../utils/constants/leagueHostsRoleDescription';
import AlertModal from '../components/AlertModal';
import { defaultAlertConfig } from '../utils/constants/general';
import AcknowledgementContent from '../components/RoleRegistration/AcknowledgementContent';
import RoleTitleDropdown from '../components/RoleTitleDropdown';

const defaultConfirmModalProps = {
  ...defaultAlertConfig,
  isConfirm: true,
  message: 'Confirm you understand the role requirements, eligibility, rewards, and community standards before registering.\nWARNING: You can only join 1 role at a time.'
}

const roleDescriptionsData = {
  founding50: founding50RoleDescription,
  dayOnes: dayOnesRoleDescription,
  influencers: influencersRoleDescription,
  leagueHosts: leagueHostsRoleDescription
};

function BetaRolesPage({
  userBetaRoleId,
  setUserBetaRoleId,
  submissionStatus,
  setSubmissionStatus,
  isUserAuthenticated,
  setIsUserAuthenticated,
  currentRoleId,
  setCurrentRoleId,
  isSubmissionSuccessful,
  setIsSubmissionSuccessful,
  showAlert = () => null, 
  setIsModalOpen = () => null
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [waitListCount, setWaitListCount] = useState(null);
  const [betaUsersCount, setBetaUsersCount] = useState(null);
  const [roleWizardData, setRoleWizardData] = useState({ ...DEFAULT_ROLE_WIZARD_DATA });
  const [confirmModalConfig, setConfirmModalConfig] = useState({ ...defaultConfirmModalProps });
  const stickyRef = useRef(null);

  const {
    isModalOpen,
    currentStepIndex,
    acceptedAcknowledgements = []
  } = roleWizardData;

  const hasRoleCatalog = BETA_ROLES.length > 0;
  const roleData = BETA_ROLES.find(role => role.id === currentRoleId) ?? {};
  const hasRoleSelection = Boolean(roleData?.id);

  let WizardContent = () => null;
  let isWizardMode = false;

  const { requiredAcknowledgements = [] } = roleData ?? {};
  const registrationSteps = roleData?.registrationSteps ?? [];
  const { isBlocking = false } = registrationSteps[currentStepIndex] ?? {};

  const showConfirmModal = () => {
    if (isUserAuthenticated) {
      const isExistingRole = Boolean(userBetaRoleId);
      setConfirmModalConfig(prev => ({
        ...prev,
        isOpen: true,
        isConfirm: isExistingRole ? false : prev.isConfirm,
        message: isExistingRole ? '' : prev.message
      }));
    } else if (!isRegistrationOpen || isCapped) {
      setConfirmModalConfig(prev => ({ ...prev, isOpen: true }));
    } else {
      setIsModalOpen(true);
    }
  };

  const resetConfirmAlert = () => {
    setConfirmModalConfig({ ...defaultConfirmModalProps });
  };

  const isStepComplete = () => {
    let isComplete = true;

    if (isBlocking && requiredAcknowledgements.length > 0) {
      isComplete = registrationSteps
        .slice(0, currentStepIndex + 1)
        .every((step) => step.requiredAcknowledgements.every(({ id }) => acceptedAcknowledgements.includes(id)));
    }

    return isComplete;
  };

  const selectRole = roleId => {
    if (!roleId) return;

    const isUserVerified = isEmailVerified();
    const userHasDisplayName = hasDisplayName();
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('roleId', roleId);
    newSearchParams.delete('isModalOpen');
    setSearchParams(newSearchParams);

    setIsUserAuthenticated(isUserVerified && userHasDisplayName);
    setCurrentRoleId(roleId);
    setWaitListCount(null);
  };

  const closeModal = () => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('isModalOpen');
    setSearchParams(newSearchParams);
    setRoleWizardData({
      ...roleWizardData,
      isModalOpen: false
    });
  };

  const cancelRoleWizard = () => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('roleId');
    newSearchParams.delete('isModalOpen');
    setSearchParams(newSearchParams);
    setRoleWizardData({ ...DEFAULT_ROLE_WIZARD_DATA });
  };

  const incrementRoleWizard = () => {
    const stepIndex = currentStepIndex + 1;

    if (stepIndex < roleData.registrationSteps.length) {
      setRoleWizardData({
        ...roleWizardData,
        currentStepIndex: stepIndex
      });
    }
  };

  const decrementRoleWizard = () => {
    const stepIndex = currentStepIndex - 1;

    if (stepIndex >= 0) {
      setRoleWizardData({
        ...roleWizardData,
        currentStepIndex: stepIndex
      });
    }
  };

  const toggleAcknowledgement = event => {
    const { target } = event;

    const isChecked = target.checked;
    const acknowledgement = target.getAttribute('data-acknowledgement');

    let nextAcknowledgements = [...roleWizardData.acceptedAcknowledgements];

    if (isChecked) {
      nextAcknowledgements.push(acknowledgement);
    } else {
      nextAcknowledgements = nextAcknowledgements.filter(ack => ack !== acknowledgement);
    }

    setRoleWizardData({
      ...roleWizardData,
      acceptedAcknowledgements: nextAcknowledgements
    });
  };

  const submitRoleWizard = async () => {
    if (isLoggedIn()) {
      const userId = auth.currentUser?.uid;

      if (!userId) return;

      await storeBetaUser(userId, currentRoleId, acceptedAcknowledgements);

      setRoleWizardData({
        ...roleWizardData,
        // submissionStatus: 'success'
      });

      setIsSubmissionSuccessful(true);
    }
  };

  const handleOnApply = async () => {
    setSubmissionStatus('submitting');
    resetConfirmAlert();
    const isUserVerified = isEmailVerified();
    const userHasDisplayName = hasDisplayName();
    const userId = getUserId();

    if (!isUserVerified || !userHasDisplayName || !userId) {
      setSubmissionStatus('idle');
      setIsModalOpen(true);
      return;
    };

    try {
      await storeBetaUser(userId, currentRoleId, acceptedAcknowledgements);
      setUserBetaRoleId(currentRoleId);
      setSubmissionStatus('success');
      setIsSubmissionSuccessful(true);
      setIsModalOpen(true);
    } catch (error) {
      console.log(error);
      setSubmissionStatus('error');
      setIsSubmissionSuccessful(false);
      showAlert(error);
    } finally {
      setSubmissionStatus('idle');
    }
  };

  const WizardButtons = () => {
    const isFirstStep = currentStepIndex <= 0;
    const isLastStep = !isFirstStep && currentStepIndex === roleData.registrationSteps.length - 1;

    return (
      <div className="d-flex justify-content-between align-items-center gap-2">
        {isWizardMode && (
          <Button
            color="link"
            onClick={isFirstStep ? cancelRoleWizard : decrementRoleWizard}
            className="text-decoration-none text-secondary fw-medium"
          >
            {currentStepIndex >= 1 && (
              <FontAwesomeIcon fixedWidth icon={faArrowLeft} className="mx-1" />
            )}
            <span>{isFirstStep ? 'Exit' : 'Previous'}</span>
          </Button>
        )}

        <Button
          outline
          disabled={isWizardMode ? !isStepComplete() : false}
          size="sm"
          color="primary"
          onClick={isWizardMode ? (isLastStep ? submitRoleWizard : incrementRoleWizard) : closeModal}
          className="ms-auto px-4 text-capitalize fw-semibold border-2"
        >
          {isWizardMode ? (isLastStep ? 'Lock In Role' : 'Next Step') : 'Done'}
        </Button>
      </div>
    );
  };

  if (isModalOpen && roleData && roleData.registrationSteps) {
    const signupCap = roleData?.limits?.signupCap;
    const isCapped = signupCap && waitListCount !== null && waitListCount >= signupCap;

    if (isUserAuthenticated) {
      if (userBetaRoleId) {
        WizardContent = ExistingRoleContent;
      } else {
        const { stepId, contentRendererKey } = roleData.registrationSteps[currentStepIndex] ?? {};

        if (stepId) {
          if (isSubmissionSuccessful) {
            WizardContent = CongratulationsContent;
          } else {
            WizardContent = wizardContentData[contentRendererKey];
            isWizardMode = true;
          }
        }
      }
    } else if (isCapped || !roleData.isRegistrationOpen) {
      WizardContent = SignupCapContent;
    } else {
      WizardContent = RegistrationContent;
    }
  }

  useEffect(() => {
    const fetchRole = async () => {
      const userId = getUserId();
      if (!userId) return;

      const isUserBetaUser = await isBetaUser(userId);
      if (!isUserBetaUser) return;

      const roleId = await getUserBetaRoleId(userId);

      setUserBetaRoleId(roleId);
    };

    fetchRole();
  }, [isModalOpen, isUserAuthenticated]);

  useEffect(() => {
    if (!isModalOpen || !roleData?.id) return;

    let isMounted = true;

    const loadWaitListCount = async () => {
      try {
        const count = await getWaitListCount(roleData.id);
        const roleUsersCount = await getRoleUsersCount(currentRoleId);
        if (isMounted) {
          setWaitListCount(count);
          setBetaUsersCount(roleUsersCount)
        }
      } catch (err) {
        console.error(err);
      }
    };

    loadWaitListCount();

    return () => {
      isMounted = false;
    };
  }, [isModalOpen, roleData?.id]);

  useEffect(() => {
    const fetchRole = async () => {
      const userId = getUserId();
      if (!userId) return;

      const isUserBetaUser = await isBetaUser(userId);
      if (!isUserBetaUser) return;

      const roleId = await getUserBetaRoleId(userId);

      setUserBetaRoleId(roleId);
      return roleId;
    };

    const unsubscribe = onAuthStateChanged(auth, () => {
      const params = new URLSearchParams(window.location.search);
      const roleId = params.get('roleId');
      const isVerified = params.get('verified') === 'true';
      const shouldOpen = params.get('isModalOpen') === 'true';

      if (!roleId) return;

      const isUserVerified = isEmailVerified();
      const userHasDisplayName = hasDisplayName();

      setRoleWizardData({
        ...DEFAULT_ROLE_WIZARD_DATA,
        isModalOpen: shouldOpen
      });

      setIsUserAuthenticated(isUserVerified && userHasDisplayName);
      setCurrentRoleId(roleId);

      if (isVerified && isUserVerified && userHasDisplayName) {
        const nextSearchParams = new URLSearchParams(params);
        nextSearchParams.delete('verified');
        setSearchParams(nextSearchParams);

        Promise.resolve(fetchRole()).then((existingRoleId) => {
          const isExistingRole = Boolean(existingRoleId);
          setConfirmModalConfig(prev => ({ ...prev, isOpen: true, isConfirm: !isExistingRole, message: isExistingRole ? '' : prev.message }))
        });
        setIsModalOpen(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const userId = getUserId();
  const userEmail = getUserEmail();
  const signupCap = roleData?.limits?.signupCap ?? 0;
  const isCapped = betaUsersCount >= signupCap;
  const { isRegistrationOpen = false } = roleData ?? {};

  return (
    <div className="page-content pb-5">
      <AlertModal
        alertConfig={{
          ...confirmModalConfig,
          message: (isCapped || !isRegistrationOpen) ? '' : confirmModalConfig.message,
          isConfirm: !(isCapped || !isRegistrationOpen) && !userBetaRoleId
        }}
        confirmButtonProps={{ textValue: 'Confirm and Join', disabled: !requiredAcknowledgements.every(({ id }) => acceptedAcknowledgements.includes(id)) }}
        handleCloseOnClick={resetConfirmAlert}
        handleOnConfirm={handleOnApply}
      >
        {(isCapped || !isRegistrationOpen) && !userBetaRoleId && (
          <SignupCapContent
            roleData={roleData}
            userId={userId}
            userEmail={userEmail}
            isUserAuthenticated={isUserAuthenticated}
            isCapped={isCapped}
            isRegistrationOpen={isRegistrationOpen}
          />
        )}

        {userBetaRoleId ? (
          <CongratulationsContent
            isBetaRolesPage={true}
            roleData={{
              title: BETA_ROLES.find(({ id }) => id === userBetaRoleId)?.title ?? '',
              discordInviteCode: BETA_ROLES.find(({ id }) => id === userBetaRoleId)?.discordInviteCode ?? ''
            }}
          />
        ) : (
          <>
            {isRegistrationOpen && !isCapped && (
              <AcknowledgementContent
                acknowledgements={requiredAcknowledgements}
                acceptedAcknowledgements={acceptedAcknowledgements}
                toggleAcknowledgement={toggleAcknowledgement}
              />
            )}
          </>
        )}
      </AlertModal>

      <section className="page-band page-band--hero section-shell surface-elevated">
        <div className="d-flex flex-column flex-lg-row align-items-start align-items-lg-center justify-content-between gap-3">
          <div>
            <h1 className="display-6 mb-2 text-uppercase text-light londrina-outline-regular">
              Beta Roles
            </h1>
            <p className="mb-0 text-secondary">
              Pick your role, review the requirements, and apply in one clean flow.
            </p>
          </div>
          <div className="d-flex flex-wrap gap-2">
            <Link to="/#signup-section" className="btn btn-outline-primary btn-cta fw-semibold border-2 px-4 text-uppercase">
              Reserve Username
            </Link>
            <ShareButton size="lg" />
          </div>
        </div>
      </section>

      <section className="page-band">
        <Container fluid className="px-0">
          <div className="d-flex flex-column gap-3" style={{ minHeight: 'calc(100vh + 250px)' }}>
            {/* Role Cards */}
            <div className="d-flex flex-column flex-md-row gap-3">
              {hasRoleCatalog ? (
                BETA_ROLES.map(item => {
                  const isSelected = item.id === currentRoleId;
                  const selectorClassName = `d-flex flex-column role-selector-btn h-100 text-start border rounded-3 py-2 px-3 ${isSelected ? 'is-selected' : ''}`;

                  return (
                    <div key={`role-button-${item.id}`}>
                      <button
                        type="button"
                        className={selectorClassName}
                        aria-pressed={isSelected}
                        aria-label={`${item.title} role. ${item?.isRegistrationOpen ? 'Intake open' : 'Waitlist only'}. ${isSelected ? 'Selected' : 'Not selected'}.`}
                        onClick={() => selectRole(item.id)}
                      >
                        <div className="lh-1">
                          <h4 className="h5 text-light mb-0 sfb-subtitle bangers-regular">
                            {item.title}
                          </h4>
                          <span className="small text-primary-orange mb-0 lh-sm sfb-subtitle">
                            {item.subtitle}
                          </span>
                        </div>
                        <p className="small text-secondary mt-2 lh-sm">
                          {item.positioning?.shortIntro}
                        </p>
                        <div className="d-flex flex-wrap gap-2 mt-auto">
                          <small className="badge text-bg-darkest border border-primary-orange-subtle text-secondary fw-medium">
                            Seats: {item?.limits?.officialCap ?? 'TBD'}
                          </small>
                          <small className="badge text-bg-darkest border border-primary-orange-subtle text-secondary fw-medium">
                            {item?.isRegistrationOpen ? 'Intake Open' : 'Waitlist'}
                          </small>
                        </div>
                      </button>
                    </div>
                  );
                })
              ) : (
                <CardText className="small text-secondary mb-0">
                  No role tracks are available yet. Return to Home and reserve your username, then check back for the next intake.
                </CardText>
              )}
              
              {/* <Card className="rounded-3 border-0 shadow-sm">
                <CardBody className="panel-card-body row gy-3 p-0">
                  
                </CardBody>
              </Card> */}
            </div>

            <div ref={stickyRef} className="position-sticky z-1" style={{ top: 110 }}>
              <Card className="panel-card rounded-3 border-0 shadow-sm">
                <CardHeader className="d-flex justify-content-between align-items-end gap-3 p-3 border-bottom border-primary-orange-subtle">
                  <div className="align-self-start">
                    <p className="small text-uppercase fw-semibold text-primary-orange-emphasis mb-1">
                      {hasRoleSelection && !isModalOpen ? 'Selected Role' : 'Role Selector'}
                    </p>
                    
                    <RoleTitleDropdown
                      currentRoleId={currentRoleId}
                      currentRoleTitle={roleData?.title}
                      items={Object.keys(roleDescriptionsData).map(key => roleDescriptionsData[key])}
                      setCurrentRoleId={setCurrentRoleId}
                    />
                  </div>

                  {hasRoleSelection && (
                    <>
                      {isModalOpen ? (
                        <div className="d-flex align-items-center gap-3 flex-fill">
                          <Badge className="panel-status bg-darker text-secondary border border-primary-orange-subtle mx-auto px-3 py-2 text-uppercase fw-semibold">In Progress</Badge>
                          <WizardButtons />
                        </div>
                      ) : (
                        <div className="d-flex flex-wrap gap-2 justify-content-lg-end">
                          <Badge className="panel-status bg-darker text-secondary border border-primary-orange-subtle px-3 py-2 text-uppercase fw-semibold">Cap: {roleData?.limits?.officialCap ?? 'TBD'}</Badge>
                          <Badge className="panel-status bg-darker text-secondary border border-primary-orange-subtle px-3 py-2 text-uppercase fw-semibold">{roleData?.isRegistrationOpen ? 'Intake Open' : 'Waitlist'}</Badge>
                        </div>
                      )}
                    </>
                  )}
                </CardHeader>

                <CardBody>
                  {hasRoleSelection ? (
                    <>
                      {isModalOpen ? (
                        <>
                          {/* Role Wizard Content */}
                          <div className="d-flex flex-column gap-3 align-items-start">
                            {isWizardMode && (
                              // <Stepper
                              //   orientation="horizontal"
                              //   labelPosition="top"
                              //   currentStepIndex={currentStepIndex}
                              //   styles={stepperStyles}
                              //   currentNodeStyle={{ transform: 'scale(120%)' }}
                              //   steps={roleData.registrationSteps.map(({ shortTitle, requiredAcknowledgements = [] }) => ({
                              //     stepLabel: shortTitle,
                              //     completed: requiredAcknowledgements.every(({ id }) => acceptedAcknowledgements.includes(id))
                              //   }))}
                              // />
                              <>THIS SHOULD NEVER SHOW</>
                            )}

                            <div className="flex-fill w-100">
                              <WizardContent
                                key={`role-wizard-${currentRoleId}-${currentStepIndex}-${isModalOpen}`}
                                isModalContent={true}
                                wizardData={roleWizardData}
                                userRoleTitle={BETA_ROLES.find(({ id }) => id === userBetaRoleId)?.title ?? ''}
                                discordInviteCode={BETA_ROLES.find(({ id }) => id === userBetaRoleId)?.discordInviteCode ?? ''}
                                roleData={roleData}
                                showAlert={showAlert}
                                toggleAcknowledgement={toggleAcknowledgement}
                              />
                            </div>
                          </div>
                        </>
                      ) : (
                        <RoleDescriptionPanel
                          stickyRef={stickyRef}
                          acceptedAcknowledgements={acceptedAcknowledgements}
                          submissionStatus={submissionStatus}
                          isApplyDisabled={!requiredAcknowledgements.every(({ id }) => acceptedAcknowledgements.includes(id))}
                          isUserAuthenticated={isUserAuthenticated}
                          isRoleCapped={isCapped}
                          roleData={roleData}
                          roleDescriptionData={roleDescriptionsData[currentRoleId]}
                          onApply={handleOnApply}
                          toggleAcknowledgement={toggleAcknowledgement}
                          showConfirmModal={showConfirmModal}
                        />
                      )}
                    </>
                  ) : (
                    <>
                      {isModalOpen ? (
                        <>
                          <CardText className="detail-copy mb-0 text-secondary">
                            The selected role is unavailable or no longer active. Pick a role from the selector to continue signup.
                          </CardText>
                          <CardText className="small text-secondary mt-3 mb-0">
                            If you opened an old link, return to the role list and start again.
                          </CardText>
                        </>
                      ) : (
                        <>
                          <CardText className="detail-copy mb-0 text-secondary fw-bold mx-auto text-center">
                            Pick a role above to start. Then review its details and finish signup.
                          </CardText>
                          <ol className="mb-0 ps-3 d-grid gap-2 text-secondary justify-content-center">
                            <li>Select your desired beta role.</li>
                            <li>Review expectations, rewards, and cap details.</li>
                            <li>Apply for the beta role.</li>
                          </ol>
                        </>
                      )}
                    </>
                  )}
                </CardBody>
              </Card>
            </div>
          </div>
        </Container>
      </section>
    </div>
  );
}

export default BetaRolesPage;
